import { isAdvisorScopeRoleId } from '@steward-app/steward-types';
import { STEWARD_ROLES, type UserProfile, type WorkspaceMembership } from '../api/types';
import type { WorkspaceKind } from '../workspace/useWorkspaceKind';

/**
 * True when the caller's membership in a workspace exists ONLY because of the advisor
 * channel — a firm's person standing in a client family's household as a guest, rather
 * than anybody's arrival into a workspace of their own.
 *
 * **The test is two-sided on purpose** (steward-app#1349): the advisor role must be
 * present, AND every other role on the membership must be an advisor-channel role
 * ({@link isAdvisorScopeRoleId} — the base advisor role, the contributor role, the sharing
 * pillars). `isClientMembership` in `src/advisor/clientMembership.ts` asks the FIRST half
 * only, deliberately, because it answers "is calling this workspace a client of the firm a
 * true statement", and a family member who also advises at the firm their own family
 * connected to is a genuine client relationship. That same person is the reason this
 * function cannot reuse it: their household carries an ordinary family role AND the advisor
 * one, they are arriving in their OWN home, and suppressing their welcome would be exactly
 * the over-suppression #1349 rules out.
 *
 * ⚠️ **This UNDER-reports — three known shapes read as family, and one of them holds the
 * advisor role.** Stated as of 2026-09-09; the first two are `AdvisorRosterScope`'s list in
 * `services/steward-app-api/src/handlers/advisor.ts`, the third was found by this change's
 * independent review and confirmed at source in that same file.
 *
 *   1. A family who invited the firm's registry address as an ordinary Viewer before
 *      pressing Connect: no role pin at all (steward-app#1001).
 *   2. Memberships minted before steward-app#651, which hold `VIEWER` because that is what
 *      `advisorRoleIds()` returned then.
 *   3. **The `adminHeld` union** (`advisor.ts`, the `needRolePin` loop, steward-app#1087,
 *      shipped 2026-09-02). When the firm's registry mailbox ALREADY holds an admin-capable
 *      role in the family's workspace, connect unions rather than replaces, producing
 *      `[ADMIN, ADVISOR, …pillars]`. The advisor role IS pinned; `every(isAdvisorScopeRoleId)`
 *      is nonetheless false, so this function answers "family" and that advisor is shown the
 *      arrival card.
 *
 * ⚠️ **Shape 3 also breaks the parity this paragraph used to claim with the other advisor
 * surfaces.** They test `includes(ADVISOR)` alone (`advisor.ts`'s roster scope), so on that
 * exact membership they answer "advisor" while this function answers "family" — the one
 * membership where the two-sided test costs something is the one where they disagree. The
 * disagreement is deliberate and is the price of not suppressing the dogfooding family
 * member, who is indistinguishable from shape 3 by role ids alone: telling them apart needs
 * a RELATIONSHIP signal (is this workspace on the caller's firm client list), which
 * `Dashboard` cannot obtain without a request on every user's home route. Accepted rather
 * than chased at the release gate — steward-app#1350 tracks it — and it needs the family to
 * have deliberately made the firm's mailbox a workspace admin first.
 *
 * An advisor seeing one card they should not have is the smaller harm; a family member
 * silently denied their arrival is the larger one, so an unrecognized role set, and shape 3,
 * both resolve to "family".
 *
 * Never an authorization decision. Appcore membership-checks every read either way; the
 * only thing this changes is what the SPA offers to show.
 */
export function isVisitingAdvisor(membership: WorkspaceMembership | null): boolean {
  const roleIds = membership?.roleIds ?? [];
  return roleIds.includes(STEWARD_ROLES.ADVISOR) && roleIds.every(isAdvisorScopeRoleId);
}

/**
 * Gating for the one-time arrival overlay ({@link ../firstrun/WelcomeOverlay}).
 * Pure so the visibility rules are unit-testable without a DOM.
 *
 * The overlay shows at most once per user (not per workspace): the moment they
 * first stand inside a workspace they can use. `welcomeSeenAt` is persisted on
 * the user's profile preferences (via /me), so the acknowledgment follows them
 * across devices — the same pattern as the getting-started dismissal (FR2).
 *
 * ⚠️ **The profile flag alone is not the rule any more** (steward-app#1349). It was, and
 * an advisor's very first `Dashboard` mount is inside a CLIENT's household — in a firm
 * workspace `HomeRoute` sends `/` to `/advisor` (and, for a caller the façade has not
 * placed at a firm, to a not-ready page instead), so the family dashboard an advisor first
 * sees belongs to somebody else either way. They were greeted with "everything organized for the
 * benefit of your family" over a family they are a guest of, and asked to name themselves.
 * So the answer now depends on the reader's relationship to the ACTIVE WORKSPACE too.
 *
 * ⚠️ **Suppression here is a RENDER decision and must never become a persisted one.** The
 * advisor's own `welcomeSeenAt` stays unwritten, so if they later have a family workspace
 * of their own they still arrive into it properly. Making the screen go away by stamping
 * the profile would spend their one arrival on somebody else's household.
 *
 * ⚠️ **"Not the caller's default workspace" is NOT a reason to suppress, and was rejected
 * as the fix.** A genuine family member accepting an invitation into a relative's household
 * is arriving somewhere that is not their default, and that is the case this overlay exists
 * for. The two things that suppress are both statements about the workspace and the
 * caller's standing in it: it is a FIRM's workspace (nobody's family), or the caller is
 * there as a visiting advisor ({@link isVisitingAdvisor}).
 *
 * The firm-workspace arm is defence in depth rather than a live path: `HomeRoute` keeps
 * `Dashboard` (the overlay's only caller) out of a firm workspace by TWO branches, not one
 * — it redirects to `/advisor` when the caller has been placed at a firm
 * (`placedAtSomeFirm`), and otherwise falls to `FirmWorkspaceNotReady` on `workspaceKind`
 * alone — so the conclusion holds either way, but "it redirects" is only half the reason.
 * This arm is here because the copy on this card is family framing, which is false in a
 * firm workspace whatever route led there, and because `workspaceKind` cannot see a firm
 * workspace that never mounted the pack (steward-app#736 — read `useWorkspaceKind`'s
 * header) so it is not a claim that a firm's staff can never see this.
 */
export function shouldShowWelcome({ profile, workspaceId, membership, workspaceKind }: {
  /** The signed-in user's profile, null until /me resolves. */
  profile: UserProfile | null;
  /** The resolved active workspace — the overlay never shows outside one. */
  workspaceId: string | null;
  /** The caller's own membership in that workspace, null until /me resolves. Null is
   *  treated as "not a visiting advisor": absence of evidence about the caller's standing
   *  must not suppress a family member's arrival. */
  membership: WorkspaceMembership | null;
  /** The active workspace's kind (`useWorkspaceKind`). */
  workspaceKind: WorkspaceKind;
}): boolean {
  if (!profile || !workspaceId) return false;
  if (workspaceKind === 'firm') return false;
  if (isVisitingAdvisor(membership)) return false;
  return !profile.preferences?.welcomeSeenAt;
}
